import OrderCard from '@/components/orders/OrderCard'
import { CustomerBlock } from '@/components/orders/show/CustomerBlock'
import { useRole } from '@/hooks/useRole'
import { ArticleService, Order, SharedProps } from '@/types'
import { formatStatus, getCustomerInfo, getStatusColor } from '@/utils/format'
import { canDeleteOrder } from '@/utils/orders'
import { Head, router, usePage } from '@inertiajs/react'
import { ActionIcon, Badge, Button, Container, Divider, Group, Menu, Stack, Text, TextInput, Title } from '@mantine/core'
import { IconArrowLeft, IconDotsVertical, IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ChangeEvent, useEffect, useState } from 'react'

export default function EditOrderPage({
  order,
  article_services,
}: {
  order: Order
  article_services: ArticleService[]
}) {
  const { current_organization } = usePage<SharedProps>().props
  const { isBoutique, isAdmin, isAccueil } = useRole()

  const [plannedDate, setPlannedDate] = useState(
    order.planned_date ? dayjs(order.planned_date).format('YYYY-MM-DD') : ''
  )

  useEffect(() => {
    setPlannedDate(order.planned_date ? dayjs(order.planned_date).format('YYYY-MM-DD') : '')
  }, [order.planned_date])

  if (!current_organization) return null
  const organizationSlug = current_organization.slug
  const articleServices = order.contract?.available_article_services ?? article_services
  const customer = getCustomerInfo(order)
  const canDelete = canDeleteOrder(order, { isAdmin, isAccueil })

  const handlePlannedDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    setPlannedDate(value)
    router.patch(
      `/organizations/${organizationSlug}/orders/${order.id}`,
      { order: { planned_date: value || null } },
      { preserveScroll: true }
    )
  }

  const goToOrder = () => router.visit(`/organizations/${organizationSlug}/orders/${order.id}`)

  const handleDeleteOrder = () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) return
    router.delete(`/organizations/${organizationSlug}/orders/${order.id}`)
  }

  return (
    <>
      <Head title={`Modifier la commande #${order.id} - ${current_organization.name}`} />

      <Container size="md" px={0}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div>
              <Group gap="sm" align="center">
                <Title order={2}>Modifier la commande #{order.id}</Title>
                <Badge color={getStatusColor(order, isBoutique)} variant="light">
                  {formatStatus(order, isBoutique)}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Créée le {dayjs(order.created_at).format('DD/MM/YYYY HH:mm')}
                {order.creator ? ` par ${order.creator.name}` : ''}
              </Text>
            </div>
            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
              <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={goToOrder}>
                Retour à la commande
              </Button>
              {canDelete && (
                <Menu position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Plus d'options">
                      <IconDotsVertical size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={handleDeleteOrder}>
                      Supprimer la commande
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>

          <Divider />

          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Text fw={700}>Informations</Text>
              <Text size="xs" c="dimmed">
                Les modifications sont enregistrées automatiquement
              </Text>
            </Group>

            <CustomerBlock
              name={customer.name}
              phoneNumber={customer.phone}
              variant={order.customer ? 'individual' : 'organization'}
            />

            <TextInput
              type="date"
              label="Date de collecte"
              value={plannedDate}
              onChange={handlePlannedDateChange}
              readOnly={isBoutique}
              maw={220}
            />
          </Stack>

          <Divider />

          <OrderCard
            order={order}
            organizationSlug={organizationSlug}
            articleServices={articleServices}
            autoOpen={false}
            action="edit"
            heading="Articles"
            embedded
            showPlannedDate={false}
            showCustomerTitle={false}
            showDeleteOrder={false}
          />
        </Stack>
      </Container>
    </>
  )
}
